// Norvig — Stories (English overlays)
// Same id-keyed shape as STORIES. Used by useStories() in stories.js.
// All translatable strings. Images, ids and metadata stay identical.

export const STORIES_EN = {
  irris: {
    name: 'Irris Hollanes Lua',
    role: 'Registered nurse',
    location: 'Helsefonna, Haugesund',
    sectorLabel: 'Health & care',
    fromCountry: 'the Philippines',
    journey: 'Philippines → Helsefonna',
    articleSubtitle: 'A journey from visitor visa to authorised nurse — without shortcuts.',
    quote: 'It is possible to begin again. It is possible to find your place.',
    pullQuote: 'The journey from Hasvik to Alta was long. But it taught me that I could. Step by step.',
    shortQuote:
      'The road to Norway was not easy. But sometimes you find a home where you least expect it. Norway is my home now.',
    teaser: 'From the Philippines to Norway — without shortcuts. Today a nurse in Haugesund.',
    stats: [
      { v: '15+', l: 'years in Norway' },
      { v: '3',  l: 'municipalities' },
      { v: '2',  l: 'authorisations' },
    ],
    timeline: [
      { year: '2011', label: 'Arrival',         text: 'Came to Norway on a visitor visa, without a word of Norwegian.' },
      { year: '2020', label: 'Healthcare worker', text: 'Worked in Finnmark, learned the language and built confidence.' },
      { year: '2022', label: 'Language exam',   text: 'Travelled from Hasvik to Alta to take the test. Passed.' },
      { year: '2023', label: 'Vindafjord',      text: 'Moved south. Began the authorisation process.' },
      { year: '2025', label: 'Nurse',           text: 'Authorised by the Directorate of Health. Hired at Helsefonna.' },
    ],
    body: [
      {
        text:
          'Irris Hollanes Lua came to Norway from the Philippines with a single visa and not a word of Norwegian. Today she works as a registered nurse at Helsefonna in Haugesund — after learning the language, having her education recognised and building herself up from a healthcare worker in Finnmark.',
        dropCap: true,
      },
      {
        text:
          'The road went through long journeys, demanding exams and sleepless nights. She especially remembers the trip from Hasvik to Alta to take the Norwegian language exam. **But she did not give up.** Step by step she built herself up — from Finnmark, to Vindafjord, to Haugesund. Today she is a nurse with experience from both medical and surgical wards, and leads KALAYAAN Norge Haugaland while serving on the Committee for Multicultural Participation in Rogaland.',
      },
    ],
    bullets: [
      'From visitor visa to authorised nurse — without shortcuts',
      'Norwegian exams, Directorate authorisation, on-the-job training',
      'Today active in multicultural work and integration',
    ],
  },
  alexandru: {
    name: 'Alexandru',
    role: 'Registered nurse',
    location: 'Municipal health service',
    sectorLabel: 'Health & care',
    fromCountry: 'Romania',
    journey: 'Travelling nurse → Permanent role',
    articleSubtitle: 'From flexibility to permanence — he found the best of two worlds.',
    quote: 'Working as a travelling nurse is like an intensive course in mastery.',
    pullQuote: 'You have to trust yourself. But it gives you a unique ability to see solutions where others see obstacles.',
    shortQuote:
      'It demands self-trust, but it gives you a unique ability to see solutions where others see obstacles.',
    teaser:
      'From Romania to Northern Norway to Southern Norway — a travelling nurse who found a permanent role in the municipality.',
    stats: [
      { v: '4+', l: 'years in Norway' },
      { v: '8+', l: 'municipalities' },
      { v: '3',  l: 'specialisations' },
    ],
    timeline: [
      { year: '2021', label: 'Arrival',        text: 'Came from Romania as an authorised nurse.' },
      { year: '2022', label: 'Locum',          text: 'Began as a travelling nurse in Northern Norway.' },
      { year: '2023', label: 'Specialisation', text: 'Experience from epilepsy treatment and autism care.' },
      { year: '2024', label: 'Family life',    text: 'The family grew — he sought stability.' },
      { year: '2025', label: 'Permanent role', text: 'Chose a permanent position in municipal health.' },
    ],
    body: [
      {
        text:
          'Alexandru came from Romania in 2021, and has since used the country as his learning ground. As a locum and travelling nurse he has worked across municipal borders — from the weather-beaten coasts of Northern Norway to the deep valleys of the south.',
        dropCap: true,
      },
      {
        text:
          'Life as a locum gave him the chance to experience everything from specialised epilepsy treatment and autism care, to the closeness of everyday home services. **But when the family grew, he sought a permanent role.** In 2025 he chose stability and predictability — and now builds deep relationships in the local community, while coming home to family every day.',
      },
    ],
    bullets: [
      'Travelling nurse across Northern and Southern Norway — broad professional experience',
      'Specialised experience: epilepsy, autism, home services',
      'From flexibility to permanence — the best of two worlds',
    ],
  },
  jurgen: {
    name: 'Jurgen',
    role: 'Seasonal worker',
    location: 'Hardanger',
    sectorLabel: 'Seasonal work',
    fromCountry: 'Albania',
    journey: 'Seasonal work → Career in finance',
    articleSubtitle: 'Seasonal work was the door in. Now he builds his career, step by step.',
    quote: 'He is not at the finish line yet, but he is on the right path.',
    pullQuote: 'Seasonal work was my way into Norway. Now I am building my career — step by step.',
    shortQuote: 'Seasonal work was my way into Norway. Now I am building my career step by step.',
    teaser:
      'From seasonal work in Hardanger toward a dream job in finance — education recognised, the language on its way.',
    stats: [
      { v: '3+', l: 'seasons' },
      { v: '1',  l: 'recognised degree' },
      { v: 'A1', l: 'Norwegian level' },
    ],
    timeline: [
      { year: '2022', label: 'First season',    text: 'Came from Albania to Hardanger.' },
      { year: '2023', label: 'Reliable hand',   text: 'Second season — earned the employer’s trust.' },
      { year: '2024', label: 'Norwegian',       text: 'Studied the language with intent between seasons.' },
      { year: '2025', label: 'Recognition',     text: 'His education in finance was recognised in Norway.' },
      { year: '2026', label: 'Towards the goal', text: 'Building network and seeking a career in finance.' },
    ],
    body: [
      {
        text:
          'Jurgen came to Norway from Albania as a seasonal worker — with a solid education in finance and a clear goal: to use his skills and build a career here. On the farm in Hardanger, where they often call him Jørgen, he proved himself across several seasons as reliable, hardworking and adaptable.',
        dropCap: true,
      },
      {
        text:
          'Between seasons he worked deliberately to learn the language, build network and earn references from Norwegian employers. **Today his education in finance is recognised in Norway** — an important step, but only part of the journey. He is not at the finish line yet, but on the right path. And that is exactly what builds a good and secure future.',
      },
    ],
    bullets: [
      'Seasonal work in Hardanger across several years — built Norwegian work experience',
      'Degree in economics and finance recognised in Norway',
      'Learning the language, building network and references step by step',
    ],
  },
  kelsey: {
    name: 'Kelsey Wilson',
    role: 'Physiotherapy assistant',
    location: 'Elderly care & rehabilitation',
    sectorLabel: 'Health & care',
    fromCountry: 'the USA',
    journey: 'USA → Norwegian elderly care',
    articleSubtitle: 'She has built a life in Norway. Now she only waits for the chance to contribute.',
    quote: 'I have found my home in Norway. Now I only wait for the chance to contribute.',
    pullQuote: 'The journey is not over, but the direction is clear — I want to contribute in Norwegian elderly care.',
    shortQuote:
      'High skill and a deep wish to contribute — the journey is not over, but the direction is clear.',
    teaser:
      'From Northern California to Norwegian elderly care — a physiotherapy assistant learning the language and awaiting recognition.',
    stats: [
      { v: '41',   l: 'years old' },
      { v: '2',    l: 'degrees' },
      { v: '2026', l: 'moved' },
    ],
    timeline: [
      { year: '2020', label: 'A.A.S. degree',  text: 'Physiotherapy assistant from Mt. Hood Community College.' },
      { year: '2024', label: 'Met her fiancé', text: 'Began planning a life in Norway.' },
      { year: '2026', label: 'Moved',          text: 'Came to Norway to build a life together.' },
      { year: 'Now',  label: 'Norwegian course', text: 'Learning the language on the path to recognition.' },
    ],
    body: [
      {
        text:
          'Kelsey Wilson is a 41-year-old physiotherapy assistant from Northern California, with a Bachelor in theatre arts from California State University, Chico, and an A.A.S. degree from Mt. Hood Community College in Oregon. She moved to Norway in 2026 to build a life together with her fiancé — and to contribute to Norwegian elderly care and rehabilitation.',
        dropCap: true,
      },
      {
        text:
          'Today she works deliberately to strengthen her Norwegian and qualify for work in the health sector, while navigating a complex recognition system. **The journey is not over, but the direction is clear.** Kelsey represents many international professionals who want to contribute in Norway — with high competence, strong motivation and a deep wish to become part of Norwegian society.',
      },
    ],
    bullets: [
      'Bachelor in theatre arts + A.A.S. as physiotherapy assistant (USA)',
      'Goal: working with the elderly in home services and care homes',
      'Learning Norwegian and awaiting recognition — determined to contribute',
    ],
  },
  alfons: {
    name: 'Alfons',
    role: 'Industrial worker',
    location: 'Boliden Odda',
    sectorLabel: 'Service & transport',
    fromCountry: 'Albania',
    journey: 'Seasonal work → Industry',
    articleSubtitle: 'From seasonal worker on Måge farm to world-leading industry at Boliden Odda.',
    quote: 'This story is not about luck. It is about opportunity.',
    pullQuote:
      'He showed up every day with the right attitude, learned quickly, took responsibility — and built trust step by step.',
    shortQuote:
      'The story of Alfons is not about luck. It is about opportunity — and seizing it.',
    teaser:
      'From Måge farm in Hardanger to Boliden Odda — a seasonal worker who became an industry asset.',
    stats: [
      { v: '7+', l: 'years in Norway' },
      { v: '3',  l: 'sectors' },
      { v: '1',  l: 'permanent role' },
    ],
    timeline: [
      { year: '2019', label: 'Arrival',        text: 'Came to Norway with a suitcase and an open mind.' },
      { year: '2022', label: 'Måge farm',      text: 'Seasonal worker in Hardanger. Long days, an unfamiliar language.' },
      { year: '2023', label: 'Trust',          text: 'Built trust through several seasons and clear willingness.' },
      { year: '2024', label: 'Construction',   text: 'First step beyond seasonal work — new industry, new relationships.' },
      { year: '2026', label: 'Boliden Odda',   text: 'Permanent role at one of the world’s leading industrial companies.' },
    ],
    body: [
      {
        text:
          'Alfons came to Norway with a suitcase, an open mind and a clear goal: to build a better future. He started as a seasonal worker on a farm in Hardanger. The work was both rewarding and demanding, the days long and the language unfamiliar. But Alfons had something that cannot be taught — willingness. He showed up every day with the right attitude, learned quickly, took responsibility and built trust step by step.',
        dropCap: true,
      },
      {
        text:
          'Across several seasons he developed not only skills, but also an understanding of Norwegian work culture: punctuality, cooperation, honesty and quality in every step. He took the initiative to learn the language, built relationships with colleagues and employers, and gathered strong references. **This was where the journey truly began.**',
      },
      {
        text:
          'Through the network he built, new opportunities opened. With experience, documented work ethic and good recommendations, Alfons eventually got the chance in a completely different industry — first in construction, then in one of the world’s leading industrial companies, Boliden Odda.',
      },
      {
        text:
          'Today he is not only an employee — he is an asset. A person the company trusts. One who contributes to value creation, stability and quality. **The story of Alfons is not about luck. It is about opportunity — and seizing it.**',
      },
    ],
    bullets: [
      'From seasonal worker on Måge farm in Hardanger',
      'Built Norwegian work experience and language step by step',
      'Permanent role at Boliden Odda — world-leading industry',
    ],
  },
  dila: {
    name: 'Dila',
    role: 'Preschool teacher',
    location: 'Aga kindergarten',
    sectorLabel: 'Education',
    fromCountry: 'Albania',
    journey: 'Seasonal work → Preschool teacher',
    articleSubtitle:
      'From seasonal worker in Hardanger to a permanent role as a preschool teacher at Aga kindergarten.',
    quote: 'She is no longer on the way — she has arrived.',
    pullQuote:
      'The days started early and were long on the farm. But after work, her real journey began.',
    shortQuote: 'She is no longer on the way — she has arrived.',
    teaser:
      'From Hardanger to Aga kindergarten — a preschool teacher whose degree was recognised through determined effort.',
    stats: [
      { v: '7+', l: 'years in Norway' },
      { v: '1',  l: 'recognised degree' },
      { v: '1',  l: 'permanent role' },
    ],
    timeline: [
      { year: '2019', label: 'Hardanger',       text: 'Came to Norway as a seasonal worker. Everything was new.' },
      { year: '2023', label: 'Norwegian',       text: 'Learned the language word by word, after work.' },
      { year: '2024', label: 'Recognition',     text: 'Began the work of getting her degree recognised.' },
      { year: '2025', label: 'Aga kindergarten', text: 'Permanent role as a preschool teacher.' },
      { year: '2026', label: 'Home',            text: 'She has arrived.' },
    ],
    body: [
      {
        text:
          'When Dila arrived in Hardanger as a seasonal worker, everything was new. The language, the people, daily life. She was far from home, but carried a quiet strength and a clear dream: to build a life in Norway and work with what she truly loved.',
        dropCap: true,
      },
      {
        text:
          'The days started early and were long on the farm. But after work, her real journey began. She practised Norwegian, word by word. She learned through conversations, through mistakes, through courage. **She did not give up, even when it was hard.**',
      },
      {
        text:
          'Dila already had a degree as a preschool teacher, but the road to having it recognised in Norway was long. Even so, she took one step at a time. She navigated systems, submitted documents, waited — and kept believing in her goal. It was not one great opportunity that changed everything. It was many small choices. Many days of effort no one saw. And then, one day, she was there.',
      },
      {
        text:
          'Today Dila works permanently at Aga kindergarten. She meets children with warmth and safety, speaks the language she once struggled with, and is an important part of a community. **She is no longer on the way — she has arrived.**',
      },
    ],
    bullets: [
      'From seasonal worker in Hardanger to preschool teacher in Norway',
      'Preschool teacher degree recognised — step by step',
      'Permanent role at Aga kindergarten, Hardanger',
    ],
  },
};
